/**
 * File: articles.ts
 *
 * Runtime client for the articles that live in `public/articles/*.md`. Those
 * files are served statically, so nothing article-related is bundled into the
 * app — it scales to any number of articles:
 *   - the list view and news banner read a small `articles/index.json` (built
 *     from every file's metadata by the `articlesIndex` Vite plugin — see
 *     vite.config.ts). It's fetched once and cached here.
 *   - the detail view lazily fetches a single article's markdown body on demand
 *     via `loadArticleHtml`, so the payload tracks what's viewed, not how many
 *     articles exist.
 *
 * The metadata shape and authoring convention (incl. the `-N` banner marker)
 * live in articles-meta.ts.
 */

import { marked } from "marked";
import type { ArticleMeta } from "./articles-meta";

export type Article = ArticleMeta;

/** How long an article stays in the news banner after its date (30 days). */
const BANNER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const INDEX_URL = `${import.meta.env.BASE_URL}articles/index.json`;

// The index is fetched once and shared by the list, banner, and detail views.
// On failure the cached promise is cleared so a later view can retry.
let indexPromise: Promise<Article[]> | null = null;

/** All articles, newest first (undated ones fall to the bottom by slug). */
export function getArticles(): Promise<Article[]> {
    if (!indexPromise) {
        indexPromise = fetch(INDEX_URL)
            .then((res) => {
                if (!res.ok) throw new Error(`articles index: ${res.status}`);
                return res.json() as Promise<Article[]>;
            })
            .then((list) =>
                [...list].sort(
                    (a, b) => b.dateSort - a.dateSort || b.slug.localeCompare(a.slug)
                )
            )
            .catch((err) => {
                indexPromise = null; // allow a retry on the next call
                throw err;
            });
    }
    return indexPromise;
}

/** A single article's metadata by slug, or `null` when unknown. */
export async function getArticle(slug: string): Promise<Article | null> {
    const list = await getArticles();
    return list.find((a) => a.slug === slug) ?? null;
}

/**
 * The single most recent banner-eligible article (filename suffixed `-N`) whose
 * date is within the last 30 days, or `null`. Drives the home-page news banner.
 */
export async function getBannerArticle(): Promise<Article | null> {
    const list = await getArticles();
    const newest = list.find((a) => a.banner && a.dateSort);
    if (!newest) return null;
    return Date.now() - newest.dateSort < BANNER_WINDOW_MS ? newest : null;
}

// Bodies are fetched on demand and cached per file for the session.
const bodyCache = new Map<string, string>();

/**
 * Lazily fetch an article's markdown body from its static file and render it to
 * HTML. The title heading is dropped from the body because the detail view
 * renders it separately in the page header. Standard article markdown semantics
 * (blank line = paragraph), not the chat's `breaks: true` behaviour.
 */
export async function loadArticleHtml(article: Article): Promise<string> {
    let raw = bodyCache.get(article.file);
    if (raw === undefined) {
        const res = await fetch(`${import.meta.env.BASE_URL}articles/${article.file}`);
        if (!res.ok) throw new Error(`article ${article.slug}: ${res.status}`);
        raw = await res.text();
        bodyCache.set(article.file, raw);
    }
    const body = raw.replace(/^\s{0,3}#\s+.+?$/m, "");
    return marked.parse(body, { gfm: true, breaks: false, async: false }) as string;
}
